import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { env } from '@/config/env';

/**
 * Google Maps API Key — sourced from environment variable (VITE_GOOGLE_MAPS_API_KEY)
 * with fallback to the default key for backward compatibility.
 */
export const GOOGLE_MAPS_API_KEY = env.GOOGLE_MAPS_API_KEY;

interface GoogleMapsContextValue {
  isLoaded: boolean;
  loadError?: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue | undefined>(undefined);

interface GoogleMapsProviderProps {
  children: ReactNode;
}

/**
 * GoogleMapsProvider
 * 
 * Centralized provider for Google Maps API loader.
 * Ensures single initialization of the Google Maps API across the entire application,
 * preventing "Loader must not be called again with different options" errors.
 * 
 * Usage:
 * 1. Wrap app in GoogleMapsProvider (done in main.tsx)
 * 2. Use useGoogleMaps() hook in any component that needs maps
 * 
 * @example
 * const { isLoaded } = useGoogleMaps();
 * 
 * if (!isLoaded) return <div>Loading maps...</div>;
 * 
 * return <GoogleMap ... />;
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    // Add any global libraries needed across all map components
    // libraries can be added here if needed in the future
  });

  const value = useMemo<GoogleMapsContextValue>(() => ({
    isLoaded,
    loadError,
  }), [isLoaded, loadError]);

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * useGoogleMaps Hook
 * 
 * Access the Google Maps loader state from any component.
 * Must be used within GoogleMapsProvider.
 * 
 * @returns {GoogleMapsContextValue} Object containing isLoaded and loadError
 * @throws {Error} If used outside GoogleMapsProvider
 * 
 * @example
 * const { isLoaded, loadError } = useGoogleMaps();
 * 
 * if (loadError) return <div>Error loading maps</div>;
 * if (!isLoaded) return <div>Loading...</div>;
 * 
 * return <GoogleMap ... />;
 */
export const useGoogleMaps = (): GoogleMapsContextValue => {
  const context = useContext(GoogleMapsContext);
  
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within GoogleMapsProvider');
  }
  
  return context;
};